// DuplicateUsersPanel — Panneau admin pour détecter et nettoyer les comptes doublons.
// Opération destructive, à utiliser avec précaution. Un doublon = 2+ comptes partageant
// exactement le même full_name (insensible à la casse).
// Pour chaque groupe, le compte le plus actif (livraisons effectuées) est pré-sélectionné
// comme "à conserver" — l'admin peut changer son choix.

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, AlertTriangle, Shield, Loader2, Trash2, Check, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DuplicateUsersPanel({ onRefresh }) {
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [keepSelection, setKeepSelection] = useState({}); // {full_name_lc: user_id}
  const [cleaning, setCleaning] = useState(null);

  const handleScan = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.name = search.trim();
      if (role) params.role = role;
      const res = await axios.get(`${API}/admin/users/find-duplicates`, { params });
      const dups = res.data?.duplicates || [];
      setDuplicates(dups);
      const defaults = {};
      dups.forEach((g) => {
        const key = (g.full_name || "").toLowerCase();
        defaults[key] = g.keep_candidate;
      });
      setKeepSelection(defaults);
      if (dups.length === 0) toast.success("Aucun doublon détecté");
      else toast.info(`${dups.length} groupe(s) de doublons détecté(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de scan");
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async (group) => {
    const key = (group.full_name || "").toLowerCase();
    const keepId = keepSelection[key];
    if (!keepId) {
      toast.error("Sélectionnez le compte à conserver");
      return;
    }
    const toRemove = group.count - 1;
    if (!window.confirm(
      `Supprimer ${toRemove} compte(s) doublon(s) de "${group.full_name}" ?\n\n` +
      `Le compte conservé recevra l'historique de livraisons des autres.\n` +
      `Cette action est IRRÉVERSIBLE.`
    )) return;

    setCleaning(key);
    try {
      const res = await axios.post(`${API}/admin/users/cleanup-duplicates`, {
        full_name: group.full_name,
        keep_user_id: keepId,
      });
      toast.success(
        `✅ ${res.data?.deleted_count || 0} compte(s) supprimé(s), ` +
        `${res.data?.migrated_orders || 0} commande(s) migrée(s)`
      );
      await handleScan();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de nettoyage");
    } finally {
      setCleaning(null);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200" data-testid="duplicate-users-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="w-5 h-5" />
            Nettoyage des comptes doublons
          </CardTitle>
          <span className="text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-200/50 px-2 py-1 rounded">
            <Shield size={10} className="inline mr-1" /> ADMIN ONLY
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">
              Nom à rechercher (ex: cheikh sadibou faye)
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-amber-700" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom partiel ou complet, insensible à la casse"
                className="pl-10 bg-white border-amber-300"
                data-testid="dup-scan-name"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">
              Rôle (optionnel)
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-10 rounded-lg border border-amber-300 bg-white px-3 text-sm"
              data-testid="dup-scan-role"
            >
              <option value="">Tous</option>
              <option value="courier">Livreur</option>
              <option value="client">Client</option>
              <option value="partner">Partenaire</option>
              <option value="artisan">Artisan</option>
              <option value="vendor">Vendeur</option>
            </select>
          </div>
          <Button
            onClick={handleScan}
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 text-white font-black"
            data-testid="dup-scan-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Scanner
          </Button>
        </div>

        {duplicates.length === 0 ? (
          <p className="text-sm text-amber-800 italic text-center py-6">
            Lancez un scan pour détecter les comptes doublons.
          </p>
        ) : (
          <div className="space-y-4">
            {duplicates.map((g) => {
              const key = (g.full_name || "").toLowerCase();
              const keepId = keepSelection[key];
              const isCleaning = cleaning === key;
              return (
                <div
                  key={key}
                  className="bg-white border-2 border-amber-300 rounded-xl p-4"
                  data-testid={`dup-group-${key.replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-black text-amber-900 flex items-center gap-2">
                        <Users size={14} /> {g.full_name}
                        <span className="text-xs bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full font-black">
                          {g.count} comptes
                        </span>
                      </p>
                    </div>
                    <Button
                      onClick={() => handleCleanup(g)}
                      disabled={isCleaning || !keepId}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      size="sm"
                      data-testid={`dup-cleanup-btn-${key.replace(/\s+/g, "-")}`}
                    >
                      {isCleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      Supprimer les {g.count - 1} doublon(s)
                    </Button>
                  </div>
                  <div className="divide-y divide-amber-100">
                    {g.accounts.map((a) => {
                      const isKept = a.id === keepId;
                      return (
                        <label
                          key={a.id}
                          className={`flex items-center gap-3 py-2 cursor-pointer rounded-lg px-2 ${
                            isKept ? "bg-emerald-50 border border-emerald-300" : "hover:bg-amber-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`keep-${key}`}
                            checked={isKept}
                            onChange={() => setKeepSelection((s) => ({ ...s, [key]: a.id }))}
                            data-testid={`dup-keep-radio-${a.id}`}
                          />
                          <div className="flex-1 text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-black text-slate-900">
                                {a.email || a.phone || "—"}
                              </span>
                              <span className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-700">{a.role}</span>
                              <span className={
                                a.is_approved
                                  ? "bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded"
                                  : "bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded"
                              }>
                                {a.is_approved ? "approuvé" : "pending"}
                              </span>
                              {a.is_blocked && <span className="bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">bloqué</span>}
                              {isKept && <span className="bg-emerald-500 text-white px-2 py-0.5 rounded font-black flex items-center gap-1"><Check size={10} /> À CONSERVER</span>}
                            </div>
                            <div className="text-slate-500 mt-0.5 font-mono text-[10px]">
                              id: {a.id} · {a.deliveries_count} livraison(s) · créé le {(a.created_at || "").substring(0, 10)}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
